import { useQuery, QueryClient } from '@tanstack/react-query';
import { getLayoutHotspots, getPaginatedHotspots } from '../api/api';
import type { LayoutHotspotListItem } from '../types/types';

/** Normalize API item to LayoutHotspotListItem (handles flat or nested hotspot shape). */
function normalizeHotspotItem(item: unknown): LayoutHotspotListItem | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const hotspot = (o.hotspot as Record<string, unknown> | undefined) || o;
  const id = hotspot.id;
  const photoId = hotspot.photo_id ?? (hotspot.photo as number) ?? (o.photo_id as number);
  if (id == null) return null;
  return {
    id: Number(id),
    photo_id: Number(photoId ?? 0),
    title: (hotspot.title as string) ?? (o.title as string) ?? null,
    pitch: Number(hotspot.pitch ?? 0),
    yaw: Number(hotspot.yaw ?? 0),
    hfov: Number(hotspot.hfov ?? 0),
    created_at: String(hotspot.created_at ?? o.created_at ?? ''),
    updated_at: String(hotspot.updated_at ?? o.updated_at ?? ''),
    overlay_count: Number(hotspot.overlay_count ?? o.overlay_count ?? 0),
    created_by_display:
      (hotspot.created_by_display as string) ??
      (hotspot.created_by as string) ??
      (o.created_by_display as string) ??
      null,
    layout_name: (hotspot.layout_name as string) ?? (o.layout_name as string) ?? null,
    photo_index:
      hotspot.photo_index != null
        ? Number(hotspot.photo_index)
        : o.photo_index != null
          ? Number(o.photo_index)
          : null,
  };
}

export interface UseLayoutHotspotsResult {
  hotspots: LayoutHotspotListItem[];
  isLoading: boolean;
  error: Error | null;
  totalHotspots: number;
  isRefetching: boolean;
  metrics: {
    totalOverlays: number;
  };
}

export interface PaginatedHotspotsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LayoutHotspotListItem[];
}

export interface UsePaginatedHotspotsResult {
  data: PaginatedHotspotsResponse | undefined;
  hotspots: LayoutHotspotListItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  metrics: {
    totalOverlays: number;
  };
}

/**
 * Hook to fetch all hotspots for a layout (ironview360: layout name, photo index, title, created_by, overlay count).
 */
export const useLayoutHotspots = (layoutId: string | undefined): UseLayoutHotspotsResult => {
  const {
    data: rawHotspots,
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey: ['layoutHotspots', layoutId],
    queryFn: () => getLayoutHotspots(layoutId!),
    enabled: !!layoutId,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, err) => {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) return false;
      return failureCount < 2;
    },
    refetchOnReconnect: 'always',
  });

  const rawList = Array.isArray(rawHotspots) ? rawHotspots : [];
  const hotspots: LayoutHotspotListItem[] = rawList
    .map(normalizeHotspotItem)
    .filter((h): h is LayoutHotspotListItem => h != null);
  const totalOverlays = hotspots.reduce((sum, h) => sum + (h.overlay_count ?? 0), 0);

  return {
    hotspots,
    isLoading,
    error: error as Error | null,
    totalHotspots: hotspots.length,
    isRefetching,
    metrics: { totalOverlays },
  };
};

export default useLayoutHotspots;

/**
 * Utility to invalidate layout hotspots cache
 */
export const invalidateLayoutHotspots = (queryClient: QueryClient, layoutId?: string) => {
  if (layoutId) {
    queryClient.invalidateQueries({ queryKey: ['layoutHotspots', layoutId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['layoutHotspots'] });
  }
};

/**
 * Hook to fetch paginated hotspots for a project (ironview360).
 * Pass searchTerm to filter server-side; when empty, all hotspots for the page are returned.
 */
export const usePaginatedHotspots = (
  projectId: string | undefined,
  page: number = 1,
  pageSize: number = 20,
  searchTerm?: string
): UsePaginatedHotspotsResult => {
  const search = searchTerm?.trim() ?? '';
  const {
    data: rawData,
    isLoading,
    isFetching,
    error,
  } = useQuery<PaginatedHotspotsResponse>({
    queryKey: ['paginatedHotspots', projectId, page, pageSize, search],
    queryFn: () => getPaginatedHotspots(projectId!, page, pageSize, search || undefined),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, err) => {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) return false;
      return failureCount < 2;
    },
    refetchOnReconnect: 'always',
    placeholderData: (previousData) => previousData,
  });

  const rawList = Array.isArray(rawData?.results) ? rawData.results : [];
  const hotspots: LayoutHotspotListItem[] = rawList
    .map(normalizeHotspotItem)
    .filter((h): h is LayoutHotspotListItem => h != null);
  const totalOverlays = hotspots.reduce((sum, h) => sum + (h.overlay_count ?? 0), 0);

  return {
    data: rawData,
    hotspots,
    isLoading,
    isFetching,
    error: error as Error | null,
    metrics: { totalOverlays },
  };
};
